import { Fragment, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Code2, GitCompareArrows } from 'lucide-react'
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap'
import {
  type ProductComparisonRowDto,
  type ProductDto,
  type ProductTargetDto,
  type ProductCompletenessSummaryDto,
  fetchProducts,
  fetchProductTargets,
  fetchProductComparisonTable,
  recordComparisonHumanDecision,
  fetchProductCompletenessSummary,
  fetchProductPimData,
  fetchRulePacks,
  type ProductDataItemDto,
  clearDistributorPimData,
  enqueueDistributorScrape,
  scrapeAndCompareTarget,
} from '../services/api'
import { comparisonRowNeedsAttention, resolveComparisonUiResult } from '../utils/comparisonResult'
import { useAuth } from '../context/AuthContext'

type FilterMode = 'all' | 'diffs' | 'missing'

interface ComparisonGroup {
  category: string
  rows: ProductComparisonRowDto[]
}

function truncateText(value: unknown, maxChars: number) {
  const s = value == null ? '' : String(value)
  if (!s) return s
  if (s.length <= maxChars) return s
  return `${s.slice(0, maxChars)}…`
}

interface PimDebugModalProps {
  show: boolean
  onHide: () => void
  productName?: string
  distributorName?: string
  pimData?: ProductDataItemDto[]
  isLoading: boolean
}

function PimDebugModal({ show, onHide, productName, distributorName, pimData, isLoading }: PimDebugModalProps) {
  const grouped = useMemo(() => {
    const bySourceAndCategory = new Map<string, Map<string, ProductDataItemDto[]>>()
    if (!pimData) return bySourceAndCategory
    for (const item of pimData) {
      const source = item.source || 'Unknown'
      if (!bySourceAndCategory.has(source)) bySourceAndCategory.set(source, new Map())
      const byCategory = bySourceAndCategory.get(source)!
      if (!byCategory.has(item.category)) byCategory.set(item.category, [])
      byCategory.get(item.category)!.push(item)
    }
    return bySourceAndCategory
  }, [pimData])

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Raw PIM data (debug){productName ? ` – ${productName}` : ''}{distributorName ? ` @ ${distributorName}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="d-flex align-items-center gap-2 text-muted">
            <Spinner animation="border" size="sm" />
            <span>Loading PIM data…</span>
          </div>
        )}
        {!isLoading && (!pimData || pimData.length === 0) && (
          <div className="text-muted small">
            No PIM data stored yet for this product. Run scrapes or import from PIM to populate.
          </div>
        )}
        {!isLoading && pimData && pimData.length > 0 && (
          <>
            <div className="small text-muted mb-2">
              Grouped by source and category. Only rows under <strong>Distributor</strong> feed the right-hand comparison column; <strong>PIM</strong> rows are Kingspan reference data.
            </div>
            {[...grouped.entries()].map(([source, byCategory]) => (
              <div key={source} className="mb-3">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <strong>{source}</strong>
                  <Badge bg="secondary">
                    {Array.from(byCategory.values()).reduce((n, items) => n + items.length, 0)} attributes
                  </Badge>
                </div>
                {Array.from(byCategory.entries()).map(([category, items]) => (
                  <div key={category} className="mb-2">
                    <div className="text-muted small fw-semibold">{category}</div>
                    <Table size="sm" bordered hover responsive className="mb-1">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '35%' }}>Field</th>
                          <th>Value</th>
                          <th style={{ width: '20%' }}>Unit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.slice(0, 20).map(i => (
                          <tr key={i.id}>
                            <td>{i.displayLabel || i.attributeName}</td>
                            <td className="text-truncate" style={{ maxWidth: 280 }}>
                              <code className="small">
                                {String(i.value).length > 240 ? `${String(i.value).slice(0, 240)}…` : i.value}
                              </code>
                            </td>
                            <td>{i.unit ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {items.length > 20 && (
                      <div className="small text-muted">{items.length - 20} more attributes hidden for brevity.</div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default function ProductCompare() {
  const { productId } = useParams<{ productId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const [filterMode, setFilterMode] = useState<FilterMode>('all')
  const [debugOpen, setDebugOpen] = useState(false)
  const [compareStatus, setCompareStatus] = useState<string | null>(null)

  const distributorIdFromQuery = searchParams.get('distributorId') || undefined
  const [selectedDistributorId, setSelectedDistributorId] = useState<string | undefined>(
    distributorIdFromQuery,
  )
  const complianceSpikeUi = import.meta.env.VITE_COMPLIANCE_SPIKE_UI === 'true'

  const {
    data: products = [],
    isLoading: productsLoading,
  } = useQuery({
    queryKey: ['products'],
    queryFn: () => fetchProducts(),
  })

  const product: ProductDto | undefined = useMemo(
    () => products.find(p => p.id === productId),
    [products, productId],
  )

  const { data: targets = [], isLoading: targetsLoading } = useQuery({
    queryKey: ['product-targets', productId],
    queryFn: () => fetchProductTargets(productId!),
    enabled: !!productId,
  })

  const primaryTarget: ProductTargetDto | undefined = useMemo(() => {
    if (selectedDistributorId) {
      return targets.find(t => t.distributorId === selectedDistributorId) ?? targets[0]
    }
    return targets[0]
  }, [targets, selectedDistributorId])

  const compareTarget: ProductTargetDto | undefined = useMemo(() => {
    if (selectedDistributorId) return primaryTarget
    if (targets.length === 1) return primaryTarget
    return undefined
  }, [primaryTarget, selectedDistributorId, targets.length])

  // `/products/:productId/compare` reuses this component across navigations; keep distributor scope
  // aligned with the current product, targets, and `?distributorId=` (stale IDs caused wrong API calls).
  useEffect(() => {
    if (!productId) return
    if (targets.length === 0) {
      setSelectedDistributorId(distributorIdFromQuery)
      return
    }
    const validIds = new Set(targets.map((t) => t.distributorId))
    const fromUrl = distributorIdFromQuery
    if (fromUrl && validIds.has(fromUrl)) {
      setSelectedDistributorId(fromUrl)
      return
    }
    if (fromUrl && !validIds.has(fromUrl)) {
      setSelectedDistributorId(targets[0]?.distributorId)
      return
    }
    setSelectedDistributorId((cur) => {
      if (cur && validIds.has(cur)) return cur
      return targets[0]?.distributorId
    })
  }, [productId, targets, distributorIdFromQuery])

  const {
    data: comparisonRows = [],
    isLoading: comparisonLoading,
    isError: comparisonQueryError,
    error: comparisonQueryErrorDetail,
    refetch: refetchComparisonTable,
  } = useQuery({
    queryKey: ['product-comparison-table', productId, selectedDistributorId],
    queryFn: () =>
      fetchProductComparisonTable(
        productId!,
        selectedDistributorId,
        true,
      ),
    enabled: !!productId,
  })

  const { data: completeness } = useQuery<ProductCompletenessSummaryDto>({
    queryKey: ['product-completeness-summary', productId],
    queryFn: () => fetchProductCompletenessSummary(productId!),
    enabled: !!productId,
  })

  const {
    data: pimData,
    isLoading: pimLoading,
  } = useQuery({
    queryKey: ['product-pim-data', productId],
    queryFn: () => fetchProductPimData(productId!),
    enabled: !!productId && debugOpen,
  })

  const { data: rulePacks = [] } = useQuery({
    queryKey: ['rulepacks', 'product-compare'],
    queryFn: fetchRulePacks,
  })

  const approvedRulePack = useMemo(() => {
    const approved = rulePacks.filter((rulePack) => rulePack.status === 'Approved')
    return approved.sort((left, right) => {
      const leftTime = left.updatedUtc ?? left.createdUtc ?? ''
      const rightTime = right.updatedUtc ?? right.createdUtc ?? ''
      return rightTime.localeCompare(leftTime)
    })[0]
  }, [rulePacks])

  const compareNowMutation = useMutation({
    mutationFn: async () => {
      if (!productId) throw new Error('Product is required.')
      if (!compareTarget) throw new Error('Select a single distributor URL before running compare.')
      if (!approvedRulePack) throw new Error('No approved rule pack is available.')

      return scrapeAndCompareTarget(compareTarget.targetId, {
        productId,
        rulePackId: approvedRulePack.id,
        reason: `Compare product now from Product Compare page (${compareTarget.url})`,
      })
    },
    onMutate: () => {
      setCompareStatus('Queueing distributor scrape and comparison workflow...')
    },
    onSuccess: (result) => {
      setCompareStatus(result.message)
      navigate(`/check-runs/${result.checkRunId}`)
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : undefined) ||
        error?.message ||
        'Failed to start scrape-and-compare workflow.'
      setCompareStatus(message)
    },
  })

  const distributorTargets = useMemo(() => {
    if (!selectedDistributorId) return targets
    return targets.filter(t => t.distributorId === selectedDistributorId)
  }, [targets, selectedDistributorId])

  // Filter pimData to the selected distributor's targets for the debug modal.
  // Kingspan/PIM source rows are always shown; Distributor rows are scoped to the selection.
  const debugPimData = useMemo(() => {
    if (!pimData) return undefined
    if (!selectedDistributorId) return pimData
    const distributorTargetIds = new Set(distributorTargets.map(t => t.targetId))
    return pimData.filter(
      item => item.source !== 'Distributor' || !item.targetId || distributorTargetIds.has(item.targetId),
    )
  }, [pimData, selectedDistributorId, distributorTargets])

  const distributorScrapeMutation = useMutation({
    mutationFn: async () => {
      if (!productId) throw new Error('Product is required.')
      const targetIds = distributorTargets.map(t => t.targetId)
      if (targetIds.length === 0) throw new Error('No distributor URLs available.')
      await Promise.all(targetIds.map(targetId => enqueueDistributorScrape(targetId)))
      return { queuedCount: targetIds.length }
    },
    onMutate: () => {
      setCompareStatus('Queueing distributor scrape...')
    },
    onSuccess: (result) => {
      setCompareStatus(`Queued distributor scrape for ${result.queuedCount} URL(s). Refresh after the jobs complete.`)
      queryClient.invalidateQueries({ queryKey: ['product-comparison-table', productId] })
      queryClient.invalidateQueries({ queryKey: ['product-pim-data', productId] })
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : undefined) ||
        error?.message ||
        'Failed to queue distributor scrape. Check distributor URLs.'
      setCompareStatus(message)
    },
  })

  const clearDistributorMutation = useMutation({
    mutationFn: async () => {
      if (!productId) throw new Error('Product is required.')
      return clearDistributorPimData(productId, selectedDistributorId)
    },
    onMutate: () => {
      setCompareStatus('Clearing distributor PIM data...')
    },
    onSuccess: (result) => {
      setCompareStatus(result.message)
      queryClient.invalidateQueries({ queryKey: ['product-comparison-table', productId] })
      queryClient.invalidateQueries({ queryKey: ['product-pim-data', productId] })
      queryClient.invalidateQueries({ queryKey: ['product-completeness-summary', productId] })
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        (typeof error?.response?.data === 'string' ? error.response.data : undefined) ||
        error?.message ||
        'Failed to clear distributor PIM data.'
      setCompareStatus(message)
    },
  })

  const humanDecisionMutation = useMutation({
    mutationFn: async (input: {
      row: ProductComparisonRowDto
      decision: 'Pass' | 'Fail'
      rationale?: string
    }) => {
      if (!productId) throw new Error('Product is required.')
      return recordComparisonHumanDecision(productId, {
        canonicalAttributeId: input.row.canonicalAttributeId,
        distributorId: selectedDistributorId,
        displayLabel: input.row.displayLabel,
        compareMode: input.row.compareMode,
        kingspanValue: input.row.kingspanValue,
        distributorValue: input.row.distributorValue,
        decision: input.decision,
        rationale: input.rationale,
      })
    },
    onSuccess: () => {
      setCompareStatus('Human decision recorded.')
      void queryClient.invalidateQueries({ queryKey: ['product-comparison-table', productId] })
    },
    onError: (error: any) => {
      const status = error?.response?.status
      const message =
        status === 403
          ? 'Author role is required to record pass/fail decisions.'
          : error?.response?.data?.message ||
            (typeof error?.response?.data === 'string' ? error.response.data : undefined) ||
            error?.message ||
            'Failed to record human decision.'
      setCompareStatus(message)
    },
  })

  const groups: ComparisonGroup[] = useMemo(() => {
    const byCategory = new Map<string, ProductComparisonRowDto[]>()
    for (const row of comparisonRows) {
      if (filterMode === 'diffs' && !comparisonRowNeedsAttention(row)) continue
      if (filterMode === 'missing' && row.kingspanStatus !== 'Missing' && row.distributorStatus !== 'Missing')
        continue
      const key = row.category || 'General'
      if (!byCategory.has(key)) byCategory.set(key, [])
      byCategory.get(key)!.push(row)
    }
    return Array.from(byCategory.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([category, rows]) => ({
        category,
        rows: rows.sort((a, b) => a.displayLabel.localeCompare(b.displayLabel)),
      }))
  }, [comparisonRows, filterMode])

  const hasDiffs = comparisonRows.some(comparisonRowNeedsAttention)
  const missingKingspan = completeness?.missingKingspanCount ?? 0

  const statusLabel = hasDiffs ? 'Attention required' : missingKingspan > 0 ? 'Incomplete' : 'Compliant'
  const statusVariant = hasDiffs ? 'warning' : missingKingspan > 0 ? 'secondary' : 'success'

  const canSeeDebug = user?.role === 'Author' || user?.role === 'MasterAdmin'
  const canRecordHumanDecision = user?.role === 'Author' || user?.role === 'MasterAdmin'

  const loading = productsLoading || targetsLoading || comparisonLoading
  const comparisonFailedMessage = comparisonQueryError
    ? (comparisonQueryErrorDetail as any)?.response?.data?.message
      ?? (typeof (comparisonQueryErrorDetail as any)?.response?.data === 'string'
        ? (comparisonQueryErrorDetail as any).response.data
        : null)
      ?? (comparisonQueryErrorDetail as Error)?.message
      ?? 'Request failed'
    : null
  const compareDisabledReason = !compareTarget
    ? 'Select one distributor URL before running compare.'
    : !approvedRulePack
      ? 'No approved rule pack is available.'
      : null

  return (
    <Container className="py-4">
      {complianceSpikeUi && (
        <Alert variant="info" className="mb-3 py-2 small">
          Focus on differences first: filter to <strong>diffs</strong> or <strong>missing</strong>. Terminology hits appear as{' '}
          <strong>TerminologyRule</strong> on Findings; value issues as <strong>ProductDataMismatch</strong>.
        </Alert>
      )}

      {comparisonQueryError && (
        <Alert variant="danger" className="mb-3 py-2">
          <div className="fw-semibold mb-1">Could not load comparison data</div>
          <div className="small mb-2">{comparisonFailedMessage}</div>
          <Button size="sm" variant="outline-danger" onClick={() => void refetchComparisonTable()}>
            Retry
          </Button>
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => navigate('/products')}
          >
            <ArrowLeft size={16} className="me-1" />
            Back to products
          </Button>
          <div>
            <h1 className="h4 mb-1">
              Comparing: {product?.name ?? 'Product'} {product?.code ? `(${product.code})` : ''}
            </h1>
            <div className="text-muted small">
              Kingspan vs {primaryTarget?.distributorName ?? primaryTarget?.distributorDomain ?? 'selected distributor'}
              {compareTarget?.url && (
                <>
                  {' · '}
                  <a href={compareTarget.url} target="_blank" rel="noreferrer">
                    View distributor product page
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="success"
            size="sm"
            disabled={!productId || compareNowMutation.isPending || !!compareDisabledReason}
            onClick={() => compareNowMutation.mutate()}
            title={compareDisabledReason ?? 'Scrape the selected distributor URL, then run comparison rules and save findings.'}
          >
            {compareNowMutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Queueing...
              </>
            ) : (
              <>
                <GitCompareArrows size={16} className="me-1" />
                Compare product now
              </>
            )}
          </Button>
          {canSeeDebug && (
            <Button
              variant="outline-secondary"
              size="sm"
              title="Show raw PIM data (debug)"
              aria-label="Show raw PIM data (debug)"
              onClick={() => setDebugOpen(true)}
            >
              <Code2 size={16} className="me-1" />
              Debug
            </Button>
          )}
        </div>
      </div>

      {compareStatus && (
        <Alert variant={compareNowMutation.isError ? 'danger' : 'info'} className="mb-3">
          {compareStatus}
          {!compareNowMutation.isError && (
            <div className="small mt-1">
              This action queues a distributor scrape for the selected comparison URL, then runs deterministic and semantic comparison rules and writes failed checks into Findings for review.
            </div>
          )}
        </Alert>
      )}

      {compareDisabledReason && (
        <Alert variant="secondary" className="mb-3 small">
          {compareDisabledReason}
        </Alert>
      )}

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button
          variant="outline-primary"
          size="sm"
          disabled={!productId || distributorScrapeMutation.isPending || distributorTargets.length === 0}
          onClick={() => distributorScrapeMutation.mutate()}
        >
          {distributorScrapeMutation.isPending ? 'Queueing...' : 'Distributor scrape/rescrape'}
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          disabled={!productId || clearDistributorMutation.isPending}
          onClick={() => {
            const label = selectedDistributorId ? 'the selected distributor' : 'all distributors'
            const confirmed = window.confirm(
              `Clear distributor PIM data for ${label}? This will remove current distributor comparison data and cannot be undone.`,
            )
            if (!confirmed) return
            clearDistributorMutation.mutate()
          }}
        >
          {clearDistributorMutation.isPending
            ? 'Clearing...'
            : selectedDistributorId
              ? 'Clear selected distributor PIM data'
              : 'Clear all distributor PIM data'}
        </Button>
      </div>

      <Row className="g-3">
        <Col lg={12}>
          <Card
            className="shadow-sm border-0"
            style={{ borderRadius: 16, overflow: 'hidden' }}
          >
            <Card.Body className="p-4">
              <div className="d-flex flex-wrap justify-content-between align-items-start mb-3 gap-3">
                <div>
                  <div className="small text-muted mb-1">Product overview</div>
                  <div className="fw-semibold">{product?.name ?? '-'}</div>
                  <div className="small text-muted">
                    {product?.code && <span className="me-2">Code: {product.code}</span>}
                    {product?.family && <span className="me-2">Family: {product.family}</span>}
                    {product?.market && <span>Market: {product.market}</span>}
                  </div>
                  {product?.kingspanUrl && (
                    <div className="small mt-1 text-truncate" style={{ maxWidth: 420 }}>
                      <a href={product.kingspanUrl} target="_blank" rel="noreferrer">
                        {product.kingspanUrl}
                      </a>
                    </div>
                  )}
                </div>
                <div className="text-end">
                  <div className="small text-muted mb-1">Comparison scope</div>
                  {compareTarget ? (
                    <>
                      <div className="fw-semibold">
                        {compareTarget.distributorName ?? compareTarget.distributorDomain ?? 'Distributor'}
                      </div>
                      <div className="small text-muted text-truncate" style={{ maxWidth: 320 }}>
                        {compareTarget.url}
                      </div>
                    </>
                  ) : (
                    <div className="small text-muted">Select a single distributor scope to run scrape and compare.</div>
                  )}
                </div>
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                <div className="d-flex flex-column gap-1 small">
                  <div className="fw-semibold" style={{ color: statusVariant === 'success' ? '#136f3c' : statusVariant === 'warning' ? '#b8860b' : '#dc3545' }}>
                    {statusLabel}
                  </div>
                  {completeness && (
                    <>
                      <div className="text-muted">
                        Kingspan missing: <span className="fw-semibold">{completeness.missingKingspanCount}</span>
                      </div>
                      <div className="text-muted">
                        Canonical attributes: <span className="fw-semibold">{completeness.totalCanonicalCount}</span>
                      </div>
                    </>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Form.Select
                    size="sm"
                    style={{ minWidth: 240 }}
                    value={selectedDistributorId ?? ''}
                    onChange={e => setSelectedDistributorId(e.target.value || undefined)}
                    disabled={targets.length === 0}
                  >
                    {targets.length === 0 ? (
                      <option value="">No distributors configured</option>
                    ) : (
                      Array.from(
                        new Map(
                          targets.map(t => [t.distributorId, t] as [string, ProductTargetDto]),
                        ).values(),
                      ).map(t => (
                        <option key={t.distributorId} value={t.distributorId}>
                          {t.distributorName ?? t.distributorDomain ?? t.distributorId}
                        </option>
                      ))
                    )}
                  </Form.Select>
                  <ToggleButtonGroup
                    type="radio"
                    name="filter-mode"
                    size="sm"
                    value={filterMode}
                    onChange={(val: FilterMode) => setFilterMode(val)}
                  >
                    <ToggleButton id="filter-all" value="all" variant="outline-secondary">
                      All fields
                    </ToggleButton>
                    <ToggleButton id="filter-diffs" value="diffs" variant="outline-secondary">
                      Differences only
                    </ToggleButton>
                    <ToggleButton id="filter-missing" value="missing" variant="outline-secondary">
                      Missing data
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </div>

              {loading && (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <Spinner animation="border" size="sm" />
                  <span>Loading comparison…</span>
                </div>
              )}

              {!loading && groups.length === 0 && (
                <div className="text-muted small">
                  No comparison data is available yet. Run Kingspan and distributor scrapes, then refresh.
                </div>
              )}

              {!loading && (
                <>
              <h2 className="h6 mt-2 mb-2">1. Product values (source of truth vs distributor)</h2>
              </>
              )}

              {!loading && groups.length > 0 && (
                <div
                  className="border rounded"
                  style={{ overflow: 'auto', maxHeight: '70vh', borderColor: '#d0d7de' }}
                >
                  <Table
                    size="sm"
                    className="mb-0 align-middle"
                    style={{ tableLayout: 'fixed', width: '100%' }}
                  >
                    <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                      <tr>
                        <th
                          style={{
                            minWidth: 200,
                            background: '#136f3c',
                            color: '#fff',
                            borderTopLeftRadius: 8,
                          }}
                        >
                          FIELD
                        </th>
                        <th style={{ width: 180, background: '#136f3c', color: '#fff' }}>RESULT</th>
                        <th style={{ minWidth: 320, background: '#136f3c', color: '#fff' }}>KINGSPAN</th>
                        <th
                          style={{
                            minWidth: 320,
                            background: '#136f3c',
                            color: '#fff',
                            borderTopRightRadius: 8,
                          }}
                        >
                          DISTRIBUTOR
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map(group => (
                        <Fragment key={group.category}>
                          <tr style={{ background: '#f6f8fa' }}>
                            <td colSpan={4} className="fw-semibold">
                              {group.category}
                            </td>
                          </tr>
                          {group.rows.map(row => {
                            const ui = resolveComparisonUiResult(row)
                            const showHumanActions =
                              canRecordHumanDecision && (ui.tone === 'review' || ui.tone === 'fail')

                            return (
                              <tr
                                key={row.canonicalAttributeId}
                              >
                                <td
                                  className="fw-semibold align-top"
                                  style={{ paddingTop: 16, paddingBottom: 16, maxWidth: 260 }}
                                >
                                  <div>{row.displayLabel}</div>
                                  <div className="text-muted small">{row.attributeName}</div>
                                </td>
                                <td
                                  className="align-top"
                                  style={{
                                    paddingTop: 16,
                                    paddingBottom: 16,
                                    width: 180,
                                    maxWidth: 180,
                                  }}
                                >
                                  <div className="small fw-semibold" style={{ color: ui.color, whiteSpace: 'normal' }}>
                                    {ui.label}
                                  </div>
                                  {row.reviewReason && (
                                    <div className="small text-muted mt-1" style={{ whiteSpace: 'normal' }}>
                                      {row.humanReviewReason
                                        ? `Automated: ${row.reviewReason}`
                                        : row.reviewReason}
                                    </div>
                                  )}
                                  {row.humanReviewReason && (
                                    <div className="small mt-1" style={{ color: '#0d6efd', whiteSpace: 'normal' }}>
                                      Human ({row.humanReviewActor ?? 'auditor'}): {row.humanReviewReason}
                                    </div>
                                  )}
                                  {row.aiPairwiseSummary && (
                                    <div className="small text-muted mt-1" style={{ whiteSpace: 'normal' }}>
                                      AI: {row.aiPairwiseSummary}
                                      {row.aiPairwiseConfidence != null
                                        ? ` (${Math.round(row.aiPairwiseConfidence * 100)}%)`
                                        : ''}
                                    </div>
                                  )}
                                  {showHumanActions && (
                                    <div className="d-flex flex-wrap gap-1 mt-2">
                                      <Button
                                        size="sm"
                                        variant="outline-success"
                                        disabled={humanDecisionMutation.isPending}
                                        onClick={() => humanDecisionMutation.mutate({ row, decision: 'Pass' })}
                                      >
                                        Confirm pass
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        disabled={humanDecisionMutation.isPending}
                                        onClick={() => humanDecisionMutation.mutate({ row, decision: 'Fail' })}
                                      >
                                        Confirm fail
                                      </Button>
                                    </div>
                                  )}
                                </td>
                                <td
                                  className="align-top"
                                  style={{
                                    paddingTop: 16,
                                    paddingBottom: 16,
                                    background: ui.kingspanBg,
                                    width: 360,
                                    maxWidth: 360,
                                  }}
                                >
                                  <div
                                    className="small"
                                    style={{
                                      maxWidth: 360,
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word',
                                    }}
                                    title={row.kingspanValue ?? ''}
                                  >
                                    {row.kingspanValue ? truncateText(row.kingspanValue, 240) : <span className="text-muted">–</span>}
                                  </div>
                                </td>
                                <td
                                  className="align-top"
                                  style={{
                                    paddingTop: 16,
                                    paddingBottom: 16,
                                    background: ui.distributorBg,
                                    width: 360,
                                    maxWidth: 360,
                                  }}
                                >
                                  {row.distributorStatus === 'Conflict' && row.distributorValues.length > 0 ? (
                                    <div className="small">
                                      {row.distributorValues.slice(0, 3).map((v, idx) => (
                                        <div key={idx} className="mb-1">
                                          <span
                                            className="d-inline-block"
                                            style={{
                                              maxWidth: 360,
                                              whiteSpace: 'normal',
                                              wordBreak: 'break-word',
                                            }}
                                            title={v.value ?? ''}
                                          >
                                            {v.value ? truncateText(v.value, 240) : '–'}
                                            {v.unit ? ` ${v.unit}` : ''}
                                          </span>
                                          {v.url && (
                                            <div className="small">
                                              <a href={v.url} target="_blank" rel="noreferrer">
                                                {v.url}
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                      {row.distributorValues.length > 3 && (
                                        <div className="small text-muted">
                                          +{row.distributorValues.length - 3} more distributor values
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div
                                      className="small"
                                      style={{
                                        maxWidth: 360,
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                      }}
                                      title={row.distributorValue ?? ''}
                                    >
                                      {row.distributorValue ? truncateText(row.distributorValue, 240) : <span className="text-muted">–</span>}
                                    </div>
                                  )}
                                  {row.reviewReason && (
                                    <div className="small text-muted mt-1">{row.reviewReason}</div>
                                  )}
                                </td>
                              </tr>
                            )
                          })}
                        </Fragment>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {!loading && (
                <div className="mt-4">
                  <h2 className="h6 mb-2">2. Compliance wording</h2>
                  <Alert variant="light" className="small mb-3 border">
                    Restricted terms and non-compliant wording are evaluated during <strong>Compare product now</strong> and
                    appear on the Findings page as <strong>TerminologyRule</strong> when the compliance spike UI is enabled.
                  </Alert>

                  <h2 className="h6 mb-2">3. Brochure version</h2>
                  <Alert variant="light" className="small mb-3 border">
                    Master brochure PDF hashes are compared after distributor scrape. Mismatches raise{' '}
                    <strong>BrochureVersionMismatch</strong> findings; structured attributes may also be extracted from distributor PDFs.
                  </Alert>

                  <h2 className="h6 mb-2">4. Product images</h2>
                  <Alert variant="secondary" className="small mb-3">
                    Image comparison (source-of-truth hero vs distributor page) is planned — not yet automated.
                  </Alert>
                </div>
              )}

              {!loading && (
                <div className="d-flex flex-wrap gap-3 align-items-center mt-3 small text-muted">
                  <div className="d-flex align-items-center gap-1">
                    <span
                      style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: '#198754',
                      }}
                    />
                    <span>Passed — deterministic or AI-confirmed match</span>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <span
                      style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: '#dc3545',
                      }}
                    />
                    <span>Failed — confirmed mismatch or conflicting distributor values</span>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <span
                      style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: '#b58100',
                      }}
                    />
                    <span>Needs review — automated check inconclusive; use Confirm pass/fail</span>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <span
                      style={{
                        display: 'inline-block',
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: '#ffc107',
                      }}
                    />
                    <span>Missing — no Kingspan or distributor value</span>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {canSeeDebug && (
        <PimDebugModal
          show={debugOpen}
          onHide={() => setDebugOpen(false)}
          productName={product?.name}
          distributorName={compareTarget?.distributorName ?? compareTarget?.distributorDomain}
          pimData={debugPimData}
          isLoading={pimLoading}
        />
      )}
    </Container>
  )
}

